import type { Bed } from '../models/Bed';
import type { Crop } from '../models/Crop';
import { adaptBbchInfo } from '../models/Planting';
import type {
  CurrentProperty,
  Planting,
  PlantingLifecycleStatus,
  PlantingLocation,
} from '../models/Planting';
import type {
  SaveWorldEntities,
  WorldBed,
  WorldField,
  WorldPlanting,
  WorldPlot,
  WorldRuntimePlanting,
} from '../types/saves';

export interface ScenarioWorldIndexes {
  fieldsById: Map<string, WorldField>;
  plotsById: Map<string, WorldPlot>;
  bedsById: Map<string, WorldBed>;
  fieldCount: number;
  plotCount: number;
}

export interface ScenarioCropSummary extends Crop {
  routeId: string;
  plantings: Planting[];
  totalQuantity: number;
  bedIds: string[];
  plotIds: string[];
}

const toKey = (value: string | number | null | undefined): string => (
  value == null ? '' : String(value)
);

const shortId = (value: string | number | null | undefined): string => {
  const normalized = toKey(value);
  if (!normalized) return '';
  return normalized.split('-')[0] || normalized;
};

const toScenarioSlug = (value: string): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'crop';
};

const buildScenarioCropRouteId = (planting: WorldPlanting): string => {
  const cropId = toKey(planting.cropId);
  if (cropId) return cropId;
  return `scenario-${toScenarioSlug(String(planting.crop || 'crop'))}`;
};

const toAnchorCode = (bed: WorldBed): string => {
  const name = String(bed.name || '').trim();
  if (!name) return shortId(bed.id);
  const stripped = name.replace(/^(bed|грядка)\s*/i, '').replace(/\s+/g, '');
  return stripped || shortId(bed.id);
};

const toPlantingStatus = (value: string | null | undefined): PlantingLifecycleStatus => {
  const normalized = String(value || 'active');
  switch (normalized) {
    case 'planned':
    case 'active':
    case 'holding':
    case 'transferred':
    case 'sold':
    case 'dead':
    case 'archived':
      return normalized;
    default:
      return 'active';
  }
};

const resolveRuntimePlantingStatus = (
  planting: WorldPlanting,
  runtime?: WorldRuntimePlanting,
): PlantingLifecycleStatus => {
  const structuralStatus = toPlantingStatus(planting.lifecycleStatus);
  const runtimeStatus = String(runtime?.lifecycleStatus || '').toLowerCase();
  const vegetationStage = String(runtime?.vegetationStage || '').toLowerCase();
  const lifecyclePhase = String(runtime?.lifecyclePhase || '').toLowerCase();

  if (['archived', 'sold', 'transferred', 'holding'].includes(structuralStatus)) {
    return structuralStatus;
  }
  if (structuralStatus === 'dead') {
    return 'dead';
  }
  if (runtime?.isDead || runtimeStatus === 'dead' || vegetationStage === 'dead' || lifecyclePhase === 'dead') {
    return 'dead';
  }
  if (['archived', 'sold', 'transferred', 'holding'].includes(runtimeStatus)) {
    return runtimeStatus as PlantingLifecycleStatus;
  }
  return structuralStatus;
};

type PlantingWithRuntimeOverlay = WorldPlanting & Partial<WorldRuntimePlanting>;

export const withPlantingRuntime = (
  planting: WorldPlanting,
  runtime?: WorldRuntimePlanting,
): PlantingWithRuntimeOverlay => ({
  ...planting,
  ...runtime,
  lifecycleStatus: resolveRuntimePlantingStatus(planting, runtime),
});

const buildCurrentProperties = (planting: PlantingWithRuntimeOverlay): CurrentProperty[] => {
  const properties: CurrentProperty[] = [];

  if (typeof planting.healthScore === 'number') {
    properties.push({
      propertyId: 'health_score',
      propertyCode: 'health_score',
      propertyName: 'Индекс здоровья',
      dataType: 'number',
      valueNumber: planting.healthScore,
      unit: '%',
    });
  }

  if (typeof planting.moisturePct === 'number') {
    properties.push({
      propertyId: 'moisture_pct',
      propertyCode: 'moisture_pct',
      propertyName: 'Влажность',
      dataType: 'number',
      valueNumber: planting.moisturePct,
      unit: '%',
    });
  }

  if (planting.lifecyclePhase) {
    properties.push({
      propertyId: 'lifecycle_phase',
      propertyCode: 'lifecycle_phase',
      propertyName: 'Фаза жизненного цикла',
      dataType: 'text',
      valueText: planting.lifecyclePhase,
    });
  }

  if (typeof planting.lifecycleGateProgressPct === 'number') {
    properties.push({
      propertyId: 'lifecycle_gate_progress_pct',
      propertyCode: 'lifecycle_gate_progress_pct',
      propertyName: 'Прогресс следующей фазы',
      dataType: 'number',
      valueNumber: planting.lifecycleGateProgressPct,
      unit: '%',
    });
  }

  if (planting.lifecycleNextPhase) {
    properties.push({
      propertyId: 'lifecycle_next_phase',
      propertyCode: 'lifecycle_next_phase',
      propertyName: 'Следующая фаза жизненного цикла',
      dataType: 'text',
      valueText: planting.lifecycleNextPhase,
    });
  }

  const bbch = adaptBbchInfo(planting);
  if (bbch?.code != null) {
    properties.push({
      propertyId: 'bbch_code',
      propertyCode: 'bbch_code',
      propertyName: 'BBCH',
      dataType: 'number',
      valueNumber: bbch.code,
    });
  }

  return properties;
};

export const buildScenarioWorldIndexes = (entities: SaveWorldEntities): ScenarioWorldIndexes => {
  const fieldsById = new Map<string, WorldField>();
  const plotsById = new Map<string, WorldPlot>();
  const bedsById = new Map<string, WorldBed>();
  const fieldIds = new Set<string>();

  entities.fields.forEach((field) => {
    const key = toKey(field.id);
    if (!key) return;
    fieldsById.set(key, field);
    fieldIds.add(key);
  });

  entities.plots.forEach((plot) => {
    const key = toKey(plot.id);
    if (!key) return;
    plotsById.set(key, plot);
    if (plot.fieldId) fieldIds.add(toKey(plot.fieldId));
  });

  entities.beds.forEach((bed) => {
    const key = toKey(bed.id);
    if (!key) return;
    bedsById.set(key, bed);
  });

  return {
    fieldsById,
    plotsById,
    bedsById,
    fieldCount: fieldIds.size,
    plotCount: entities.plots.length,
  };
};

export const buildScenarioBed = (
  bed: WorldBed,
  indexes: ScenarioWorldIndexes,
): Bed => {
  const plotId = toKey(bed.plotId);
  const plot = plotId ? indexes.plotsById.get(plotId) : undefined;
  const fieldId = toKey(plot?.fieldId);
  const field = fieldId ? indexes.fieldsById.get(fieldId) : undefined;

  return {
    id: toKey(bed.id),
    plotId,
    name: bed.name || `BED-${shortId(bed.id)}`,
    area: bed.area ?? null,
    geo_location: null,
    plotSummary: plot
      ? { id: toKey(plot.id), name: plot.name ?? null }
      : plotId
        ? { id: plotId, name: null }
        : undefined,
    fieldSummary: field
      ? { id: toKey(field.id), name: field.name ?? null }
      : fieldId
        ? { id: fieldId, name: null }
        : undefined,
    farmSummary: null as any,
  };
};

export const buildScenarioPlantingLocation = (
  planting: WorldPlanting,
  indexes: ScenarioWorldIndexes,
): PlantingLocation | null => {
  const bedId = toKey(planting.bedId);
  if (!bedId) return null;

  const bed = indexes.bedsById.get(bedId);
  const plotId = toKey(planting.plotId || bed?.plotId);
  const plot = plotId ? indexes.plotsById.get(plotId) : undefined;
  const fieldId = toKey(planting.fieldId || plot?.fieldId);
  const field = fieldId ? indexes.fieldsById.get(fieldId) : undefined;

  return {
    id: bedId,
    bedId,
    fromDate: planting.originDate ?? null,
    toDate: null,
    bed: {
      id: bedId,
      name: bed?.name ?? null,
      plot_id: plotId || null,
      anchor_code: bed ? toAnchorCode(bed) : shortId(bedId),
    },
    plot: plotId
      ? {
          id: plotId,
          field_id: fieldId || null,
          name: plot?.name ?? null,
        }
      : null,
    field: fieldId
      ? {
          id: fieldId,
          name: field?.name ?? null,
        }
      : null,
    farm: null,
  };
};

export const buildScenarioPlanting = (
  planting: PlantingWithRuntimeOverlay,
  indexes: ScenarioWorldIndexes,
): Planting => {
  const location = buildScenarioPlantingLocation(planting, indexes);
  const cropName = planting.crop || 'Культура';
  const id = toKey(planting.id);
  const cropRouteId = buildScenarioCropRouteId(planting);

  return {
    id,
    uuid: id,
    publicId: id,
    crop: {
      id: cropRouteId,
      name: cropName,
      latinName: '',
    },
    plantingType: planting.quantity && planting.quantity > 1 ? 'batch' : 'single',
    quantity: planting.quantity ?? 1,
    lifecycleStatus: toPlantingStatus(planting.lifecycleStatus),
    originDate: planting.originDate ?? null,
    autoHarvest: planting.autoHarvest === true,
    locations: location ? [location] : [],
    locationContext: {
      farmsTotal: 1,
      fieldsTotal: indexes.fieldCount,
      plotsTotal: indexes.plotCount,
    },
    bbch: adaptBbchInfo(planting),
    currentProperties: buildCurrentProperties(planting),
    photos: [],
  };
};

export const buildScenarioCropSummaries = (
  entities: SaveWorldEntities,
  indexes: ScenarioWorldIndexes = buildScenarioWorldIndexes(entities),
  runtimePlantings: Record<string, WorldRuntimePlanting> = {},
): ScenarioCropSummary[] => {
  const crops = new Map<string, {
    crop: ScenarioCropSummary;
    bedIds: Set<string>;
    plotIds: Set<string>;
  }>();

  entities.plantings.forEach((rawPlanting) => {
    const plantingWithRuntime = withPlantingRuntime(rawPlanting, runtimePlantings[String(rawPlanting.id)]);
    const planting = buildScenarioPlanting(plantingWithRuntime, indexes);
    const routeId = buildScenarioCropRouteId(plantingWithRuntime);
    const existing = crops.get(routeId);
    const bedId = toKey(plantingWithRuntime.bedId);
    const plotId = toKey(plantingWithRuntime.plotId || planting.locations[0]?.plot?.id);

    if (existing) {
      existing.crop.plantings.push(planting);
      existing.crop.plantingsCount = (existing.crop.plantingsCount || 0) + 1;
      existing.crop.totalQuantity += planting.quantity ?? 1;
      if (bedId) existing.bedIds.add(bedId);
      if (plotId) existing.plotIds.add(plotId);
      return;
    }

    const crop: ScenarioCropSummary = {
      id: routeId,
      name: planting.crop.name,
      latinName: planting.crop.latinName,
      plantingsCount: 1,
      routeId,
      plantings: [planting],
      totalQuantity: planting.quantity ?? 1,
      bedIds: [],
      plotIds: [],
    };

    crops.set(routeId, {
      crop,
      bedIds: new Set(bedId ? [bedId] : []),
      plotIds: new Set(plotId ? [plotId] : []),
    });
  });

  return Array.from(crops.values())
    .map(({ crop, bedIds, plotIds }) => ({
      ...crop,
      bedIds: Array.from(bedIds),
      plotIds: Array.from(plotIds),
    }))
    .sort((left, right) => left.name.localeCompare(right.name, 'ru'));
};

export const findScenarioCropSummary = (
  entities: SaveWorldEntities,
  cropId: string | number | null | undefined,
  indexes: ScenarioWorldIndexes = buildScenarioWorldIndexes(entities),
  runtimePlantings: Record<string, WorldRuntimePlanting> = {},
): ScenarioCropSummary | null => {
  if (cropId == null) return null;
  return buildScenarioCropSummaries(entities, indexes, runtimePlantings)
    .find((crop) => String(crop.routeId) === String(cropId)) || null;
};
